import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// THE SOLUTION CALCULATOR
// Window Washing Guide — Tool 03 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// Purpose: Scale any window cleaning recipe to any volume, in the units
// you actually have. Save formulas. Print labels.
//
// Built on the canonical recipes from the article system — Dawn + distilled
// is the house standard. Other presets are documented with their actual
// use cases, not generic "all-purpose" claims.
//
// ─────────────────────────────────────────────────────────────────────────

// Volume conversions to milliliters (the internal base unit)
const TO_ML = {
  drop:    0.05,    // 1 drop ≈ 0.05 mL (standard kitchen dropper)
  ml:      1,
  tsp:     4.929,
  tbsp:    14.787,
  oz:      29.574,
  cup:     236.588,
  pint:    473.176,
  quart:   946.353,
  gallon:  3785.41,
  liter:   1000,
};

const UNIT_LABELS = {
  drop:   'drops',
  ml:     'mL',
  tsp:    'tsp',
  tbsp:   'tbsp',
  oz:     'fl oz',
  cup:    'cups',
  pint:   'pints',
  quart:  'quarts',
  gallon: 'gallons',
  liter:  'liters',
};

// ─── Preset recipes ──────────────────────────────────────────────────────
// Each recipe is defined per 1 gallon of finished solution (the trade unit)
// then scaled. Ingredients have a base amount and unit; we convert to mL
// internally for math and back to the user's preferred output unit.
const PRESETS = [
  {
    id: 'house',
    name: 'The House Standard',
    italic: 'Dawn + distilled',
    use: 'General residential glass. The recipe behind every "professional" job. Cuts dirt without leaving surfactant haze.',
    note: 'Original blue Dawn only. Ultra and Platinum varieties contain conditioners that streak.',
    rating: 'Definitive',
    ingredients: [
      { name: 'Original Dawn dish soap', amount: 2, unit: 'drop' },
      { name: 'Distilled water',          amount: 1, unit: 'gallon' },
    ],
    method: 'Combine in a clean bucket. Apply with sea sponge or microfiber sleeve. Squeegee top to bottom.',
  },
  {
    id: 'vinegar',
    name: 'The Vinegar Cut',
    italic: 'maintenance only',
    use: 'Dissolves light mineral haze on glass that has been over-cleaned. A maintenance pass, not a primary cleaner.',
    note: 'Smells terrible. Do not mix with bleach-based cleaners. Skip on stone-trimmed windows — the acid etches limestone.',
    rating: 'Conditional',
    ingredients: [
      { name: 'White distilled vinegar (5%)', amount: 0.5, unit: 'gallon' },
      { name: 'Distilled water',              amount: 0.5, unit: 'gallon' },
    ],
    method: 'Spray, let sit 30 seconds, agitate with microfiber, rinse with plain distilled water, squeegee dry.',
  },
  {
    id: 'cold',
    name: 'The Cold-Weather Mix',
    italic: 'sub-freezing work',
    use: 'Window cleaning below 32°F. The alcohol depresses the freezing point so the solution does not flash-ice on contact with the glass.',
    note: 'Fire risk. Keep away from open flame. Increase IPA ratio for temperatures below 20°F.',
    rating: 'Specialty',
    ingredients: [
      { name: '70% isopropyl alcohol', amount: 1,  unit: 'cup' },
      { name: 'Original Dawn',         amount: 2,  unit: 'drop' },
      { name: 'Distilled water',       amount: 14, unit: 'cup' },
    ],
    method: 'Mix in a sprayer. Work in small sections. Squeegee immediately — the IPA accelerates evaporation.',
  },
  {
    id: 'pro',
    name: 'The Pro Concentrate',
    italic: 'route work',
    use: 'High-volume professional cleaning. GG3 or Ettore Squeegee-Off, diluted heavier than the bottle suggests because pros cut everything.',
    note: 'Bottle dilution is for amateurs. The trade dilution is roughly half.',
    rating: 'Trade-grade',
    ingredients: [
      { name: 'GG3 or Squeegee-Off concentrate', amount: 0.5, unit: 'oz' },
      { name: 'Distilled water',                  amount: 1,   unit: 'gallon' },
    ],
    method: 'Mix in a 5-gallon bucket. Apply with a sleeve, squeegee with a fresh rubber. Replace rubber every 8–10 hours of work.',
  },
  {
    id: 'restoration',
    name: 'The Restoration Pass',
    italic: 'use once',
    use: 'For glass with built-up mineral etching. CLR neutralizes calcium carbonate. Used as a one-time treatment, not a regular recipe.',
    note: 'Test on a small corner first. Will damage tinted, low-E, and laminated glass. See the Tint Identifier before using.',
    rating: 'Aggressive',
    ingredients: [
      { name: 'CLR Calcium Lime Rust', amount: 4,  unit: 'oz' },
      { name: 'Distilled water',       amount: 12, unit: 'oz' },
    ],
    method: 'Apply with a microfiber, NEVER a sponge. Let dwell 60 seconds. Rinse twice with distilled water. Follow with the House Standard.',
  },
];

// ─── helpers ─────────────────────────────────────────────────────────────
function toMl(amount, unit) {
  return amount * TO_ML[unit];
}

function fromMl(ml, unit) {
  return ml / TO_ML[unit];
}

// Convert a recipe (defined in its native units) to a target total volume,
// returning each ingredient scaled to a sensible output unit.
function scaleRecipe(recipe, targetMl) {
  const baseTotalMl = recipe.ingredients.reduce(
    (sum, ing) => sum + toMl(ing.amount, ing.unit),
    0
  );
  const scale = targetMl / baseTotalMl;

  return recipe.ingredients.map((ing) => {
    const scaledMl = toMl(ing.amount, ing.unit) * scale;
    const outUnit = pickBestUnit(scaledMl, ing.unit);
    const outAmount = fromMl(scaledMl, outUnit);
    return {
      name: ing.name,
      amount: outAmount,
      unit: outUnit,
      mlExact: scaledMl,
    };
  });
}

// Pick a sensible output unit. Stay in the same unit family when possible
// (drops stay drops if they're countable; otherwise step up).
function pickBestUnit(ml, originalUnit) {
  if (originalUnit === 'drop' && ml < 5) return 'drop';
  if (ml < 5) return 'ml';
  if (ml < 15) return 'tsp';
  if (ml < 50) return 'tbsp';
  if (ml < 240) return 'oz';
  if (ml < 950) return 'cup';
  if (ml < 3500) return 'quart';
  return 'gallon';
}

// Format a numeric amount cleanly for display
function fmt(n, unit) {
  if (unit === 'drop') return Math.round(n).toString();
  if (n < 1) return n.toFixed(2);
  if (n < 10) return n.toFixed(1);
  return Math.round(n).toString();
}

// ─── main component ──────────────────────────────────────────────────────
export default function SolutionCalculator() {
  const [selectedId, setSelectedId] = useState('house');
  const [batchAmount, setBatchAmount] = useState(32);
  const [batchUnit, setBatchUnit] = useState('oz');
  const [dark, setDark] = useState(false);
  const [savedFormulas, setSavedFormulas] = useState([]);
  const [showLabel, setShowLabel] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customRecipe, setCustomRecipe] = useState({
    name: 'My Mix',
    ingredients: [
      { name: '', amount: 0, unit: 'oz' },
      { name: '', amount: 0, unit: 'oz' },
    ],
  });
  const [toast, setToast] = useState('');

  const selected = customMode ? customRecipe : PRESETS.find((p) => p.id === selectedId);
  const targetMl = toMl(parseFloat(batchAmount) || 0, batchUnit);
  const scaled = (selected && targetMl > 0) ? scaleRecipe(selected, targetMl) : [];

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  }

  function saveFormula() {
    if (!selected) return;
    const formula = {
      id: Date.now(),
      name: selected.name,
      batchAmount: parseFloat(batchAmount),
      batchUnit,
      ingredients: scaled.map((s) => ({ ...s })),
      savedAt: new Date().toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      }),
    };
    setSavedFormulas([formula, ...savedFormulas].slice(0, 6));
    showToast('Formula saved');
  }

  function quickBatch(amount, unit) {
    setBatchAmount(amount);
    setBatchUnit(unit);
  }

  function addCustomIngredient() {
    setCustomRecipe({
      ...customRecipe,
      ingredients: [...customRecipe.ingredients, { name: '', amount: 0, unit: 'oz' }],
    });
  }

  function updateCustomIng(i, field, val) {
    const next = [...customRecipe.ingredients];
    next[i] = { ...next[i], [field]: field === 'amount' ? parseFloat(val) || 0 : val };
    setCustomRecipe({ ...customRecipe, ingredients: next });
  }

  function removeCustomIng(i) {
    if (customRecipe.ingredients.length <= 2) return;
    setCustomRecipe({
      ...customRecipe,
      ingredients: customRecipe.ingredients.filter((_, idx) => idx !== i),
    });
  }

  // ─── theme tokens ──────────────────────────────────────────────────────
  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/solution-calculator" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes toastIn {
          from { opacity: 0; transform: translate(-50%, 8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }
        .ing-row { animation: slideIn 0.4s cubic-bezier(.2,.7,.3,1) both; }

        .preset-tile {
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .preset-tile:hover {
          background: ${T.surface};
        }
        .preset-tile.active {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
        }

        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .ghost-btn:hover { color: ${T.sienna}; }
        .quick-batch:hover { border-color: ${T.ink}; color: ${T.ink}; }
        .quick-batch.active { border-color: ${T.ink}; color: ${T.ink}; background: ${T.surface}; }

        input.field, select.field {
          background: transparent;
          border: none;
          border-bottom: 1px solid ${T.rule};
          color: ${T.ink};
          font-family: ${fontBody};
          padding: 8px 0;
          transition: border-color 0.2s;
        }
        input.field:focus, select.field:focus {
          outline: none;
          border-bottom-color: ${T.sienna};
        }
        select.field { cursor: pointer; padding-right: 16px; }

        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-label { box-shadow: 0 0 0 1px #000 !important; }
        }

        @media (max-width: 720px) {
          .grid-main { grid-template-columns: 1fr !important; }
          .h1-title { font-size: 38px !important; line-height: 1.05 !important; }
          .qty-display { font-size: 56px !important; }
        }
      `}</style>

      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: T.ink,
            color: T.bg,
            padding: '12px 20px',
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            zIndex: 100,
            animation: 'toastIn 0.3s cubic-bezier(.2,.7,.3,1) both',
          }}
        >
          ◆ {toast.toUpperCase()}
        </div>
      )}

      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* ─── Header ──────────────────────────────────────────── */}
        <header
          className="no-print"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingBottom: 20,
            borderBottom: `1px solid ${T.rule}`,
            marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            aria-label="Toggle dark mode"
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* ─── Tool framing ────────────────────────────────────── */}
        <div className="reveal reveal-1 no-print">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 03 / 09 &nbsp;·&nbsp; PRINT-READY
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 60, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            The Solution{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Calculator
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 680, margin: '0 0 12px',
            }}
          >
            Five working recipes. Scale any of them to any volume of any unit.
            Save the formulas you actually use. Print labels for the bottles.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            All recipes tested by working cleaners. All ratios opinionated.
          </p>
        </div>

        {/* ─── Two-column layout ───────────────────────────────── */}
        <div
          className="grid-main reveal reveal-2"
          style={{
            display: 'grid',
            gridTemplateColumns: '320px 1fr',
            gap: 56,
            marginBottom: 80,
          }}
        >
          {/* ─── LEFT: Preset picker ─────────────────────────── */}
          <div className="no-print">
            <div
              style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                marginBottom: 20, paddingBottom: 12,
                borderBottom: `1px solid ${T.rule}`,
              }}
            >
              § THE FORMULAS
            </div>

            {PRESETS.map((p, i) => (
              <button
                key={p.id}
                className={`preset-tile ${!customMode && selectedId === p.id ? 'active' : ''}`}
                onClick={() => { setSelectedId(p.id); setCustomMode(false); setShowLabel(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  borderLeft: '2px solid transparent',
                  padding: '16px 16px 16px 18px',
                  cursor: 'pointer',
                  marginBottom: 4,
                  fontFamily: fontBody,
                  color: T.ink,
                }}
              >
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.12em', color: T.inkFaint,
                    marginBottom: 6,
                  }}
                >
                  № 0{i + 1} &nbsp;·&nbsp; {p.rating.toUpperCase()}
                </div>
                <div
                  style={{
                    fontFamily: fontDisplay, fontSize: 19,
                    lineHeight: 1.2, marginBottom: 4,
                  }}
                >
                  {p.name}
                </div>
                <div
                  style={{
                    fontFamily: fontDisplay, fontStyle: 'italic',
                    fontSize: 13, color: T.sienna,
                  }}
                >
                  {p.italic}
                </div>
              </button>
            ))}

            <button
              className="preset-tile"
              onClick={() => { setCustomMode(true); setShowLabel(false); }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderLeft: customMode ? `2px solid ${T.sienna}` : '2px solid transparent',
                padding: '16px 16px 16px 18px',
                cursor: 'pointer',
                marginTop: 12,
                marginBottom: 4,
                fontFamily: fontBody,
                color: T.ink,
                borderTop: `1px solid ${T.rule}`,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.12em', color: T.inkFaint,
                  marginBottom: 6, marginTop: 12,
                }}
              >
                № 06 &nbsp;·&nbsp; YOUR OWN
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 19,
                  lineHeight: 1.2, marginBottom: 4,
                }}
              >
                Custom Mix +
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontStyle: 'italic',
                  fontSize: 13, color: T.sienna,
                }}
              >
                build your own
              </div>
            </button>
          </div>

          {/* ─── RIGHT: Active recipe + scaling ──────────────── */}
          <div>
            {selected && !customMode && (
              <PresetView
                preset={selected}
                T={T}
                fontDisplay={fontDisplay}
                fontMono={fontMono}
                fontBody={fontBody}
              />
            )}

            {customMode && (
              <CustomBuilder
                recipe={customRecipe}
                setName={(n) => setCustomRecipe({ ...customRecipe, name: n })}
                update={updateCustomIng}
                add={addCustomIngredient}
                remove={removeCustomIng}
                T={T}
                fontDisplay={fontDisplay}
                fontMono={fontMono}
                fontBody={fontBody}
              />
            )}

            {/* ─── Volume control ───────────────────────────── */}
            <div
              className="no-print"
              style={{
                marginTop: 40,
                paddingTop: 40,
                borderTop: `1px solid ${T.rule}`,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 24,
                }}
              >
                ◆ SCALE TO BATCH SIZE
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 20,
                  marginBottom: 28,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={batchAmount}
                  onChange={(e) => setBatchAmount(e.target.value)}
                  className="qty-display"
                  style={{
                    flex: '0 1 200px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${T.ink}`,
                    fontFamily: fontDisplay,
                    fontSize: 80,
                    fontWeight: 300,
                    letterSpacing: '-0.02em',
                    color: T.ink,
                    padding: '4px 0 4px',
                    minWidth: 0,
                    width: '100%',
                    outline: 'none',
                  }}
                />
                <select
                  value={batchUnit}
                  onChange={(e) => setBatchUnit(e.target.value)}
                  className="field"
                  style={{
                    fontFamily: fontMono, fontSize: 14,
                    letterSpacing: '0.1em', minWidth: 120,
                  }}
                >
                  <option value="oz">FL OZ</option>
                  <option value="cup">CUPS</option>
                  <option value="pint">PINTS</option>
                  <option value="quart">QUARTS</option>
                  <option value="gallon">GALLONS</option>
                  <option value="ml">MILLILITERS</option>
                  <option value="liter">LITERS</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  [16, 'oz', '16oz spray bottle'],
                  [32, 'oz', '32oz spray bottle'],
                  [1, 'gallon', '1 gallon'],
                  [5, 'gallon', '5 gallon bucket'],
                  [1, 'liter', '1 liter'],
                ].map(([amt, unit, label]) => (
                  <button
                    key={label}
                    className={`quick-batch ${batchAmount == amt && batchUnit === unit ? 'active' : ''}`}
                    onClick={() => quickBatch(amt, unit)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.rule}`,
                      color: T.inkSoft,
                      fontFamily: fontMono,
                      fontSize: 10,
                      letterSpacing: '0.1em',
                      padding: '8px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {label.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* ─── Scaled output ───────────────────────────── */}
            {scaled.length > 0 && (
              <div
                style={{
                  marginTop: 40,
                  background: T.surface,
                  borderLeft: `2px solid ${T.sienna}`,
                  padding: '32px 36px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 24,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.sienna,
                    }}
                  >
                    ⚡ YOUR BATCH
                  </div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.12em', color: T.inkFaint,
                    }}
                  >
                    {batchAmount} {UNIT_LABELS[batchUnit].toUpperCase()} TOTAL
                  </div>
                </div>

                <h3
                  style={{
                    fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
                    lineHeight: 1.2, letterSpacing: '-0.02em',
                    margin: '0 0 24px',
                  }}
                >
                  {selected.name}
                </h3>

                <div style={{ borderTop: `1px solid ${T.rule}` }}>
                  {scaled.map((ing, i) => (
                    <div
                      key={i}
                      className="ing-row"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        gap: 20,
                        padding: '16px 0',
                        borderBottom: `1px solid ${T.rule}`,
                        alignItems: 'baseline',
                        animationDelay: `${i * 0.05}s`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: fontMono, fontSize: 10,
                          letterSpacing: '0.12em', color: T.inkFaint,
                        }}
                      >
                        № 0{i + 1}
                      </div>
                      <div
                        style={{
                          fontFamily: fontBody, fontSize: 15,
                          color: T.ink, lineHeight: 1.4,
                        }}
                      >
                        {ing.name || <span style={{ color: T.inkFaint, fontStyle: 'italic' }}>unnamed ingredient</span>}
                      </div>
                      <div
                        style={{
                          fontFamily: fontDisplay,
                          fontSize: 22, fontWeight: 400,
                          color: T.ink,
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmt(ing.amount, ing.unit)}{' '}
                        <span style={{ fontFamily: fontMono, fontSize: 11, color: T.inkFaint, letterSpacing: '0.1em' }}>
                          {UNIT_LABELS[ing.unit]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action row */}
                <div
                  className="no-print"
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginTop: 28,
                  }}
                >
                  <button
                    className="pill-btn"
                    onClick={saveFormula}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.ink}`,
                      color: T.ink,
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em',
                      padding: '12px 22px', cursor: 'pointer',
                      transition: 'background 0.2s, color 0.2s',
                    }}
                  >
                    SAVE FORMULA
                  </button>
                  <button
                    className="pill-btn"
                    onClick={() => setShowLabel(!showLabel)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.ink}`,
                      color: T.ink,
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em',
                      padding: '12px 22px', cursor: 'pointer',
                      transition: 'background 0.2s, color 0.2s',
                    }}
                  >
                    {showLabel ? 'HIDE LABEL' : 'PRINT LABEL →'}
                  </button>
                </div>
              </div>
            )}

            {/* ─── Print Label ──────────────────────────────── */}
            {showLabel && scaled.length > 0 && (
              <PrintLabel
                recipe={selected}
                scaled={scaled}
                batchAmount={batchAmount}
                batchUnit={batchUnit}
                T={T}
                fontDisplay={fontDisplay}
                fontMono={fontMono}
                fontBody={fontBody}
              />
            )}
          </div>
        </div>

        {/* ─── Saved formulas ─────────────────────────────────── */}
        {savedFormulas.length > 0 && (
          <div className="reveal reveal-3 no-print" style={{ marginBottom: 64 }}>
            <div
              style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                marginBottom: 20, paddingBottom: 12,
                borderBottom: `1px solid ${T.rule}`,
                display: 'flex', justifyContent: 'space-between',
              }}
            >
              <span>§ YOUR SAVED FORMULAS</span>
              <span style={{ color: T.inkFaint }}>{savedFormulas.length} OF 6</span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 0,
              }}
            >
              {savedFormulas.map((f, i) => (
                <div
                  key={f.id}
                  style={{
                    padding: '16px 20px 16px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    borderRight: i % 2 === 0 ? `1px solid ${T.rule}` : 'none',
                    paddingRight: i % 2 === 0 ? 24 : 0,
                    paddingLeft: i % 2 === 1 ? 24 : 0,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.12em', color: T.inkFaint,
                      marginBottom: 6,
                    }}
                  >
                    SAVED {f.savedAt.toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 18,
                      marginBottom: 4, color: T.ink,
                    }}
                  >
                    {f.name}
                  </div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      color: T.inkFaint, letterSpacing: '0.05em',
                    }}
                  >
                    {f.batchAmount} {UNIT_LABELS[f.batchUnit]} batch
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Cross-links ────────────────────────────────────── */}
        <div
          className="reveal reveal-4 no-print"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 0,
            borderTop: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}
        >
          {[
            ['DIAGNOSTICIAN', 'The vinegar question: when it works', '/articles/vinegar-question-when-it-works'],
            ['THE TOOLS', 'Hard Water Severity Scorer ←', '/tools/hard-water-scorer'],
            ['ENCYCLOPEDIA', 'Surfactants: a working primer', '/articles/house-standard-surfactants-reference'],
          ].map(([kicker, title, to]) => (
            <Link
              key={title}
              to={to}
              className="ghost-btn"
              style={{
                padding: '20px 0',
                borderBottom: `1px solid ${T.rule}`,
                textDecoration: 'none',
                color: T.ink,
                transition: 'color 0.2s',
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}
              >
                {kicker}
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 17,
                  lineHeight: 1.3, paddingRight: 16,
                }}
              >
                {title}
              </div>
            </Link>
          ))}
        </div>

        {/* ─── Footer ───────────────────────────────────────── */}
        <footer
          className="no-print"
          style={{
            marginTop: 40,
            paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 042 &nbsp;·&nbsp; TOOL 03 OF 09</span>
          <Link
            to="/tools/solution-calculator/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}

// ─── Preset detail view ──────────────────────────────────────────────────
function PresetView({ preset, T, fontDisplay, fontMono, fontBody }) {
  return (
    <div>
      <div
        style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 16,
        }}
      >
        ◆ {preset.rating.toUpperCase()}
      </div>

      <h2
        style={{
          fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
          lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: '0 0 6px',
        }}
      >
        {preset.name}
      </h2>

      <div
        style={{
          fontFamily: fontDisplay, fontStyle: 'italic',
          fontSize: 22, color: T.sienna, fontWeight: 400,
          marginBottom: 24,
        }}
      >
        {preset.italic}
      </div>

      <p
        style={{
          fontFamily: fontDisplay, fontSize: 18, fontWeight: 300,
          lineHeight: 1.55, color: T.inkSoft,
          margin: '0 0 24px', maxWidth: 580,
        }}
      >
        {preset.use}
      </p>

      <div
        style={{
          borderLeft: `2px solid ${T.rule}`,
          padding: '4px 0 4px 16px',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.18em', color: T.inkFaint,
            marginBottom: 6,
          }}
        >
          ⌘ NOTE
        </div>
        <div
          style={{
            fontFamily: fontBody, fontSize: 14,
            color: T.ink, lineHeight: 1.5,
          }}
        >
          {preset.note}
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.18em', color: T.inkFaint,
            marginBottom: 8,
          }}
        >
          METHOD
        </div>
        <div
          style={{
            fontFamily: fontBody, fontSize: 14,
            color: T.ink, lineHeight: 1.55,
          }}
        >
          {preset.method}
        </div>
      </div>
    </div>
  );
}

// ─── Custom recipe builder ───────────────────────────────────────────────
function CustomBuilder({ recipe, setName, update, add, remove, T, fontDisplay, fontMono, fontBody }) {
  return (
    <div>
      <div
        style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 16,
        }}
      >
        ◆ YOUR OWN
      </div>

      <input
        type="text"
        value={recipe.name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name your mix"
        className="field"
        style={{
          fontFamily: fontDisplay, fontSize: 36, fontWeight: 400,
          lineHeight: 1.1, letterSpacing: '-0.025em',
          width: '100%', marginBottom: 24,
          borderBottom: `1px solid ${T.rule}`,
        }}
      />

      <p
        style={{
          fontFamily: fontDisplay, fontSize: 17, fontWeight: 300,
          lineHeight: 1.55, color: T.inkSoft,
          margin: '0 0 32px', maxWidth: 580,
        }}
      >
        Define your base ratios. The calculator will scale them to whatever
        batch size you need below.
      </p>

      <div style={{ borderTop: `1px solid ${T.rule}` }}>
        {recipe.ingredients.map((ing, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 110px auto',
              gap: 16,
              padding: '14px 0',
              borderBottom: `1px solid ${T.rule}`,
              alignItems: 'baseline',
            }}
          >
            <input
              type="text"
              value={ing.name}
              onChange={(e) => update(i, 'name', e.target.value)}
              placeholder={i === 0 ? 'e.g. Original Dawn' : 'Ingredient'}
              className="field"
              style={{ fontFamily: fontBody, fontSize: 14 }}
            />
            <input
              type="number"
              min="0"
              step="0.1"
              value={ing.amount || ''}
              onChange={(e) => update(i, 'amount', e.target.value)}
              placeholder="Amount"
              className="field"
              style={{
                fontFamily: fontDisplay, fontSize: 18,
                textAlign: 'right',
              }}
            />
            <select
              value={ing.unit}
              onChange={(e) => update(i, 'unit', e.target.value)}
              className="field"
              style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.1em',
              }}
            >
              <option value="drop">DROPS</option>
              <option value="ml">ML</option>
              <option value="tsp">TSP</option>
              <option value="tbsp">TBSP</option>
              <option value="oz">FL OZ</option>
              <option value="cup">CUPS</option>
              <option value="quart">QUARTS</option>
              <option value="gallon">GALLONS</option>
            </select>
            <button
              onClick={() => remove(i)}
              disabled={recipe.ingredients.length <= 2}
              style={{
                background: 'transparent', border: 'none',
                color: T.inkFaint, fontSize: 18,
                cursor: recipe.ingredients.length <= 2 ? 'not-allowed' : 'pointer',
                opacity: recipe.ingredients.length <= 2 ? 0.3 : 1,
                padding: '4px 8px',
              }}
              aria-label="Remove ingredient"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={add}
        style={{
          marginTop: 16,
          background: 'transparent', border: 'none',
          color: T.sienna,
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em',
          cursor: 'pointer', padding: '8px 0',
        }}
      >
        + ADD INGREDIENT
      </button>
    </div>
  );
}

// ─── Print label ─────────────────────────────────────────────────────────
function PrintLabel({ recipe, scaled, batchAmount, batchUnit, T, fontDisplay, fontMono, fontBody }) {
  const printIt = () => window.print();

  return (
    <div style={{ marginTop: 32 }}>
      <div
        className="no-print"
        style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.inkFaint,
          marginBottom: 16,
        }}
      >
        § PRINT-READY LABEL — 4×3 IN
      </div>

      <div
        className="print-label"
        style={{
          background: '#FAF7F1',
          color: '#1A1D1C',
          border: `1px solid ${T.rule}`,
          padding: '28px 32px',
          maxWidth: 460,
          fontFamily: fontBody,
          boxShadow: dark => '0 4px 20px rgba(0,0,0,0.06)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, letterSpacing: '0.2em', color: '#A39E91',
            marginBottom: 12, paddingBottom: 8,
            borderBottom: '1px solid #D9D3C5',
          }}
        >
          WINDOW WASHING GUIDE &nbsp;·&nbsp; HOUSE FORMULA
        </div>

        <h4
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 28, fontWeight: 400,
            lineHeight: 1.05, letterSpacing: '-0.02em',
            margin: '0 0 4px', color: '#1A1D1C',
          }}
        >
          {recipe.name}
        </h4>
        {recipe.italic && (
          <div
            style={{
              fontFamily: "'Fraunces', serif",
              fontStyle: 'italic', fontSize: 14,
              color: '#B5532A', marginBottom: 16,
            }}
          >
            {recipe.italic}
          </div>
        )}

        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, letterSpacing: '0.15em',
            color: '#A39E91', marginBottom: 8,
          }}
        >
          {batchAmount} {UNIT_LABELS[batchUnit].toUpperCase()} BATCH
        </div>

        <div style={{ borderTop: '1px solid #D9D3C5', marginTop: 8 }}>
          {scaled.map((ing, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '10px 0',
                borderBottom: '1px solid #D9D3C5',
                fontSize: 13,
              }}
            >
              <span style={{ color: '#1A1D1C' }}>
                {ing.name || 'ingredient'}
              </span>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 16, fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(ing.amount, ing.unit)}{' '}
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#A39E91', letterSpacing: '0.1em' }}>
                  {UNIT_LABELS[ing.unit]}
                </span>
              </span>
            </div>
          ))}
        </div>

        {recipe.method && (
          <div
            style={{
              marginTop: 14,
              fontSize: 11,
              lineHeight: 1.5,
              color: '#5A5A52',
              fontStyle: 'italic',
              fontFamily: "'Fraunces', serif",
            }}
          >
            {recipe.method}
          </div>
        )}

        <div
          style={{
            marginTop: 16, paddingTop: 10,
            borderTop: '1px solid #D9D3C5',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 8, letterSpacing: '0.15em',
            color: '#A39E91',
            display: 'flex', justifyContent: 'space-between',
          }}
        >
          <span>WINDOWWASHINGGUIDE.COM</span>
          <span>{new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}</span>
        </div>
      </div>

      <button
        className="no-print pill-btn"
        onClick={printIt}
        style={{
          marginTop: 16,
          background: 'transparent',
          border: `1px solid ${T.ink}`,
          color: T.ink,
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em',
          padding: '12px 22px', cursor: 'pointer',
          transition: 'background 0.2s, color 0.2s',
        }}
      >
        PRINT LABEL →
      </button>
    </div>
  );
}
